import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Loader2, File as FileIcon } from 'lucide-react';
import { documentsAPI } from '@/lib/api';
import { toast } from 'sonner';

// ── File-kind detection ──────────────────────────────────────────────────────

function kindFor(doc) {
  const name = (doc.filename || doc.title || '').toLowerCase();
  const type = (doc.content_type || '').toLowerCase();
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name)) return 'image';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.docx')) return 'docx';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'xlsx';
  return 'unsupported';
}

// ── Preview modal ─────────────────────────────────────────────────────────────
// Renders images/PDFs natively and .docx/.xlsx client-side (mammoth / xlsx).
// Anything else (e.g. .pptx) falls back to a "download to view" message —
// there's no good lightweight in-browser renderer for it.

export default function DocumentPreviewModal({ doc, onClose, onDownload }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [content, setContent] = useState(null); // { kind, url } | { kind: 'docx'|'xlsx', html }
  const kind = doc ? kindFor(doc) : 'unsupported';

  useEffect(() => {
    if (!doc) return;
    let objectUrl = null;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(false);
      setContent(null);

      if (kind === 'unsupported') {
        setLoading(false);
        return;
      }

      try {
        const res = await documentsAPI.getContent(doc._id);
        const buf = res.data;
        if (cancelled) return;

        if (kind === 'image' || kind === 'pdf') {
          const blob = new Blob([buf], { type: doc.content_type || (kind === 'pdf' ? 'application/pdf' : undefined) });
          objectUrl = window.URL.createObjectURL(blob);
          setContent({ kind, url: objectUrl });
        } else if (kind === 'docx') {
          const mammoth = await import('mammoth');
          const { value: html } = await mammoth.convertToHtml({ arrayBuffer: buf });
          setContent({ kind, html });
        } else if (kind === 'xlsx') {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(buf, { type: 'array' });
          const firstSheet = wb.SheetNames[0];
          const html = XLSX.utils.sheet_to_html(wb.Sheets[firstSheet], { id: 'preview-sheet' });
          setContent({ kind, html });
        }
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [doc?._id]);

  if (!doc) return null;

  return (
    <Dialog open={!!doc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="truncate">{doc.title}</DialogTitle>
            <Button size="sm" variant="outline" className="gap-1.5 flex-shrink-0" onClick={() => onDownload(doc)}>
              <Download className="w-3.5 h-3.5" /> Download
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-[#F1ECE3] bg-[#FBF8F2]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-[#9AA3AB]">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-[#9AA3AB] text-sm">
              <FileIcon className="w-8 h-8" />
              Couldn't load a preview for this file.
            </div>
          ) : kind === 'unsupported' ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-[#9AA3AB] text-sm px-6 text-center">
              <FileIcon className="w-8 h-8" />
              Preview isn't available for this file type yet — download to view it.
            </div>
          ) : content?.kind === 'image' ? (
            <div className="h-full flex items-center justify-center p-4">
              <img src={content.url} alt={doc.title} className="max-w-full max-h-full object-contain" />
            </div>
          ) : content?.kind === 'pdf' ? (
            <iframe src={content.url} title={doc.title} className="w-full h-full border-0" />
          ) : content?.kind === 'docx' ? (
            <div
              className="prose prose-sm max-w-none p-6 bg-white m-2 rounded-lg shadow-sm"
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          ) : content?.kind === 'xlsx' ? (
            <div
              className="p-4 text-xs [&_table]:border-collapse [&_td]:border [&_td]:border-[#E3DDD1] [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-[#E3DDD1] [&_th]:px-2 [&_th]:py-1 [&_th]:bg-[#F1ECE3]"
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
